import api, { type ApiResponse } from "./api";

export type PublisherDashboardApp = {
  id: string;
  name: string;
  slug: string;
  status: string;
  short_description: string | null;
  icon_url: string | null;
  subscription_status: string | null;
  subscription_expires_at: string | null;
  days_remaining: number;
  downloads: number;
  rating: number | null;
  rating_count: number;
  current_version: string | null;
};

export type PublisherDashboardSummary = {
  displayName: string | null;
  accountStatus: "active" | "suspended";
  membershipStatus: "Active" | "Inactive";
  totalApps: number;
};

export type PublisherDashboardData = {
  publisher: PublisherDashboardSummary;
  apps: PublisherDashboardApp[];
};

export type PublisherDashboardLocationState = {
  publishMessage?: string | null;
  selectedApplicationId?: string;
};

export const SELECTED_APP_DOWNLOADS_LABEL = "App Downloads";

export function buildPublisherDashboardNavigationState(
  applicationId: string,
  publishMessage?: string | null,
): PublisherDashboardLocationState {
  return {
    selectedApplicationId: applicationId,
    ...(publishMessage ? { publishMessage } : {}),
  };
}

export function resolvePublisherDashboardSelection(
  apps: Pick<PublisherDashboardApp, "id" | "status" | "subscription_status">[],
  options?: {
    requestedApplicationId?: string | null;
    currentApplicationId?: string | null;
  },
): string | null {
  const requestedId = options?.requestedApplicationId;
  if (requestedId) {
    if (apps.length === 0 || apps.some((app) => app.id === requestedId)) {
      return requestedId;
    }
  }

  const currentId = options?.currentApplicationId;
  if (currentId && apps.some((app) => app.id === currentId)) {
    return currentId;
  }

  return pickDefaultApplicationId(apps);
}

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending review",
  revision_requested: "Revision requested",
  approved: "Approved",
  published: "Published",
  rejected: "Rejected",
  suspended: "Suspended",
  delisted: "Delisted",
};

export function formatApplicationStatusLabel(status: string | null | undefined): string {
  if (!status) {
    return "Unknown";
  }

  return APPLICATION_STATUS_LABELS[status] ?? status.replaceAll("_", " ");
}

export async function fetchPublisherDashboard(): Promise<PublisherDashboardData> {
  const [dashboardResponse, appsResponse] = await Promise.all([
    api.get<ApiResponse<{ publisher: PublisherDashboardSummary }>>(
      "/publisher/dashboard",
    ),
    api.get<ApiResponse<{ apps: PublisherDashboardApp[] }>>("/publisher/apps"),
  ]);

  return {
    publisher: dashboardResponse.data.data.publisher,
    apps: appsResponse.data.data.apps,
  };
}

export function pickDefaultApplicationId(
  apps: Pick<PublisherDashboardApp, "id" | "status" | "subscription_status">[],
): string | null {
  if (apps.length === 0) {
    return null;
  }

  const published = apps.find((app) => app.status === "published");
  if (published) {
    return published.id;
  }

  const activeSubscription = apps.find(
    (app) => app.subscription_status === "active",
  );
  if (activeSubscription) {
    return activeSubscription.id;
  }

  return apps[0].id;
}
